import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import useLocalStorage from "../../../hooks/useLocalStorage";
import { apiPaths } from "../../../service/apiPaths";
import ApiService from "../../../service/ApiService";
import {
  formatDateToISODateString,
  limit,
  monthNames,
  updateComponent,
  ViewType,
} from "../../../util/constant";
import { ILead, IService } from "./Lead.props";

interface StatusBreakdown {
  status: string;
  count: number;
}

interface WeekData {
  week: string;
  totalCount: number;
  statusBreakdown: StatusBreakdown[];
}

interface MonthData {
  month: string;
  totalLeads: number;
  weeks: WeekData[];
}

interface StatusCount {
  status: string;
  count: number;
}

interface GraphData {
  weekWiseTotalLeads: never[];
  monthWiseData: MonthData[];
  overallStatusCounts: StatusCount[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

const useLead = () => {
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [leadData, setLeadData] = useState<ILead[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);

  // Use localStorage for persistent state
  const [search, setSearch] = useLocalStorage<string>("lead-search", "");
  const [page, setPage] = useLocalStorage<number>("lead-page", 1);
  const [sortValue, setSortValue] = useLocalStorage<string>("lead-sort", "");
  const [csvStartDate, setCsvStartDate] = useLocalStorage<Date | null>(
    "lead-csv-start",
    new Date()
  );
  const [csvEndDate, setCsvEndDate] = useLocalStorage<Date | null>(
    "lead-csv-end",
    new Date()
  );
  const [dateFilter, setDateFilter] = useLocalStorage<string>(
    "lead-date-filter",
    ""
  );

  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedLeadForEmail, setSelectedLeadForEmail] = useState<
    string | null
  >(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [todayCount, setTodayCount] = useState(0);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [leadName, setLeadName] = useState<string>("");
  const [isEmail, setIsEmail] = useState();
  const [isRemindeMe, setIsRemindeMe] = useState();
  const [isEmailSent, setIsEmailSent] = useState();
  const navigate = useNavigate();
  const today = new Date();
  const [leadCount, setLeadCount] = useState({
    newLead: 0,
    inProgress: 0,
  });
  const [services, setServices] = useState<IService[]>([]);
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [user, setUser] = useState<any[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  const fetchServicesData = async (_event: any, name: string) => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.services}`,
        data: { type: name },
      });
      setServices(result);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformsData = async (name: string) => {
    try {
      setLoading(true);
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.platforms}`,
        data: {
          type: name,
        },
      });
      if (result && result.data) {
        setPlatforms(result.data);
      } else if (result && Array.isArray(result)) {
        setPlatforms(result);
      } else {
        console.warn("Unexpected platforms data structure:", result);
        setPlatforms([]);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatformsData("platform");
  }, []);

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      const isSearchActive = typeof search === "string" && search.trim() !== "" && search !== "Reminder";

      // Build base parameters
      let params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        isLead: 'lead'
      });

      // If searching, add search parameter
      if (isSearchActive) {
        params.append('search', search);
      }

      // Handle date filter options
      let actualFromDate = fromDate;
      let actualToDate = toDate;

      if (dateFilter && dateFilter !== "all") {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);

        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);

        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);

        const currentWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        currentWeek.setDate(today.getDate() - daysToMonday);
        const currentWeekEnd = new Date(currentWeek);
        currentWeekEnd.setDate(currentWeek.getDate() + 6);

        const nextWeek = new Date(currentWeek);
        nextWeek.setDate(currentWeek.getDate() + 7);

        switch (dateFilter) {
          case "today":
            actualFromDate = today;
            actualToDate = today;
            break;
          case "yesterday":
            actualFromDate = yesterday;
            actualToDate = yesterday;
            break;
          case "lastWeek":
            actualFromDate = lastWeek;
            actualToDate = today;
            break;
          case "lastMonth":
            actualFromDate = lastMonth;
            actualToDate = today;
            break;
          case "last7Days":
            actualFromDate = last7Days;
            actualToDate = today;
            break;
          case "currentWeek":
            actualFromDate = currentWeek;
            actualToDate = currentWeekEnd;
            break;
          case "nextWeek":
            actualFromDate = currentWeek;
            actualToDate = nextWeek;
            break;
          default:
            actualFromDate = fromDate;
            actualToDate = toDate;
        }
      }

      // Add date parameters if available
      if (actualFromDate && actualToDate) {
        const formattedFromDate = formatDateToISODateString(actualFromDate);
        const formattedToDate = formatDateToISODateString(actualToDate);
        params.append('fromDate', formattedFromDate);
        params.append('toDate', formattedToDate);
      }

      // Add sort value if available (and not already searching)
      if (!isSearchActive && sortValue && sortValue !== "select" && sortValue !== "") {
        params.append('search', sortValue);
      }

      // Build final endpoint
      const endpoint = `${apiPaths.getAllLeads}?${params.toString()}`;

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      const emailSentResponse = result?.data?.emailResponse?.filter(
        (email: any) => email.status === true
      );
      const emailSent = result?.data?.filter(
        (email: any) => email.emailSent === true
      );

      setLeadData(result?.data);
      setIsEmail(emailSent);
      setUser(result?.users);
      setServices(result.service || []);
      if (!platforms || platforms.length === 0) {
        setPlatforms(result.platform || []);
      }
      setIsRemindeMe(result?.isReminder);
      setIsEmailSent(emailSentResponse);
      setTotalPage(result.totalPages);
      setTodayCount(result?.totalTodayRemindersCount);
      setLeadCount({
        newLead: result.newCount,
        inProgress: result.withoutNewCount,
      });
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page !== undefined) {
      handleDateFilter(null, null);
    }
  }, [page, search, sortValue, dateFilter]);

  useEffect(() => {
    if (search !== undefined && search !== "") {
      setPage(1);
    }
  }, [search]);

  useEffect(() => {
    if (dateFilter !== undefined && dateFilter !== "") {
      setPage(1);
    }
  }, [dateFilter]);

  const handleConfirmStatusUp = async (id?: any) => {
    setLoading(true);
    const leadId = id || deletingId;
    const data = {
      isClient: true,
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.convertLead}${deletingId}`,
      });
      handleDateFilter(null, null);
    } catch (error) {
      console.error("Error deleting lead:", error);
    } finally {
      setLoading(false);
    }
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleGraph = async (monthName?: string) => {
    setLoading(true);
    try {
      let monthNumber;
      if (monthName) {
        monthNumber = monthNames.indexOf(monthName) + 1;
      } else {
        monthNumber = new Date().getMonth() + 1;
      }

      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.graphLead}?month=${monthNumber}`,
      });
      setGraphData(response);
    } catch (error) {
      console.error("Error fetching graph data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    setSelectedLeadId(id);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };

  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addLead");
  };

  const updateScreen = (type: ViewType) => {
    updateComponent(type, () => {}, setSelectedLeadId);
  };

  const handleRowSelection = (rowId: string) => {
    setSelectedRows((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      } else {
        return [...prev, rowId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.length === leadData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(leadData.map((lead) => lead._id));
    }
  };

  const handleSendEmail = async (message: string) => {
    return console.log("message", message);
  };

  const handleSendComment = async (message: string) => {
    try {
      const leadIds = selectedLeadForEmail
        ? [selectedLeadForEmail]
        : selectedRows;
      if (leadIds.length === 0) {
        toast.error("Please select at least one lead");
        return;
      }
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateLead}${leadIds}`,
        data: {
          comments: message,
        },
      });

      toast.success(`Email sent to ${leadIds.length} lead(s)`);
      setSelectedRows([]);
      setSelectedLeadForEmail(null);
      setIsEmailModalOpen(false);
    } catch (error) {
      toast.error(`Error sending email: ${error}`);
    }
  };

  const openEmailModal = (leadId?: string) => {
    if (leadId) {
      setSelectedLeadForEmail(leadId);
    }
    setIsEmailModalOpen(true);
  };

  const handleSelectStatus = (e: any, id: any) => {
    const status = e.target.value;
    const reminderDate = id.reminderDate;
    handleConfirmLeadStatusUp(id, status, reminderDate);
  };

  const handleConfirmLeadStatusUp = async (
    id: any,
    status: any,
    reminderDate?: Date
  ) => {
    setLoading(true);
    const updateData = {
      leadStatus: status,
      ...(reminderDate && { reminderDate: reminderDate }),
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.leadStatus}${id._id}`,
        data: updateData,
      });
      toast.success("Lead status updated successfully");
    } catch (error: any) {
      console.error("Error updating Lead status:", error);
      toast.error(
        error?.response?.data?.message || "Error updating Lead status"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await ApiService({
        endpoint: `${apiPaths.deleteLead}${deletingId}`,
        method: "DELETE",
      });
      toast.success("Lead is Deleted Successfully");
      handleDateFilter(null, null);
    } catch (error) {
      toast.error("Error in Deleting Lead");
    } finally {
      setShowDeleteConfirmation(false);
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeletingId(null);
  };

  const handleDownloadCSV = async () => {
    setIsDownloadModalOpen(true);
  };

  const handleConfirmDownload = async (selectedFields?: string[], platformFilter?: string) => {
    if (!csvStartDate || !csvEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
  
    setLoading(true);
    try {
      // Ensure csvStartDate and csvEndDate are Date objects
      const startDateObj =
        typeof csvStartDate === "string"
          ? new Date(csvStartDate)
          : csvStartDate;
      const endDateObj =
        typeof csvEndDate === "string" ? new Date(csvEndDate) : csvEndDate;
  
      const formattedStartDate = formatDateToISODateString(startDateObj);
      const formattedEndDate = formatDateToISODateString(endDateObj);
  
      // Get all leads within date range without pagination
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllLeads}?fromDate=${formattedStartDate}&toDate=${formattedEndDate}&isLead=lead&limit=10000`,
      });
  
      let leads = result?.data || [];
  
      // Filter by platform if specified
      if (platformFilter && platformFilter !== "") {
        leads = leads.filter((lead: ILead) => lead.platform?.name === platformFilter);
      }
  
      if (leads.length === 0) {
        toast.error("No leads found for the selected date range and filters");
        setLoading(false);
        return;
      }
  
      // Map for field to value extraction - FIXED VERSION
      const fieldValueMap: Record<
        string,
        (lead: ILead, index: number) => string | number
      > = {
        "No": (_lead, index) => index + 1,
        "LEADID": (lead) => lead._id || "",
        "SERVICES": (lead) => lead.serviceActive || lead.services?.type || "",
        "DESCRIPTION": (lead) => lead.description || "",
        "EMAIL": (lead) => lead.email || "",
        "PHONE": (lead) => lead.phone || "",
        "Lead Web Link": (lead) => lead.linkedin || "",
        "TEMPLATE": (lead) => lead.services?.description || "",
        // Additional fields that might be useful
        "userId": (lead) => lead.userId?.name || "",
        "linkedin": (lead) => lead.linkedin || "",
        "services": (lead) => lead.serviceActive || lead.services?.type || "",
        "description": (lead) => lead.description || "",
        "leadStatus": (lead) => lead.leadStatus || "",
        "connectedId": (lead) => lead.connectedId?.name || "",
        "lookingFor": (lead) => lead.lookingFor || "",
        "platform": (lead) => lead.platform?.name || "",
        "name": (lead) => lead.name || "",
        "companyName": (lead) => lead.companyName || "",
        "title": (lead) => lead.title || "",
        "createdAt": (lead) => lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : "",
      };
  
      // Use selectedFields or default
      const headers =
        selectedFields && selectedFields.length > 0
          ? selectedFields
          : [
              "No",
              "LEADID",
              "SERVICES",
              "DESCRIPTION",
              "EMAIL",
              "PHONE",
              "Lead Web Link",
            ];
  
      // Generate CSV data
      const csvData = leads.map((lead: ILead, index: number) => {
        const row: Record<string, string | number> = {};
        headers.forEach((header) => {
          // First check if we have a custom mapping for this header
          if (fieldValueMap[header]) {
            row[header] = fieldValueMap[header](lead, index);
          } 
          // Then check if the lead has this property directly
          else if (lead.hasOwnProperty(header)) {
            const value = lead[header as keyof ILead];
            // Handle nested objects
            if (value && typeof value === 'object') {
              if ('name' in value) {
                row[header] = String(value.name ?? "");
              } else if ('type' in value) {
                row[header] = String(value.type ?? "");
              } else {
                row[header] = JSON.stringify(value);
              }
            } else {
              row[header] = String(value ?? "");
            }
          } 
          // Default to empty string
          else {
            row[header] = "";
          }
        });
        return row;
      });
  
      // Create CSV content with proper escaping
      const escapeCsvValue = (value: string | number): string => {
        const stringValue = String(value ?? "");
        // If value contains comma, newline, or quotes, wrap in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
  
      const csvContent = [
        headers.join(","),
        ...csvData.map((row: Record<string, string | number>) =>
          headers.map((header) => escapeCsvValue(row[header] ?? "")).join(",")
        ),
      ].join("\n");
  
      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const platformSuffix = platformFilter ? `_${platformFilter}` : "";
      link.setAttribute(
        "download",
        `leads_${formattedStartDate}_to_${formattedEndDate}${platformSuffix}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      const downloadMessage = platformFilter 
        ? `CSV downloaded successfully (${platformFilter} platform - ${leads.length} leads)`
        : `CSV downloaded successfully (All platforms - ${leads.length} leads)`;
      toast.success(downloadMessage);
      setIsDownloadModalOpen(false);
    } catch (error) {
      toast.error("Error downloading CSV");
      console.error("Error downloading CSV:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDownload = () => {
    setIsDownloadModalOpen(false);
  };

  const handleRemainder = async () => {
    setLoading(true);
    try {
      let endpoint = `${
        apiPaths.getAllLeads
      }?page=${page}&limit=${limit}&isLead=lead&search=${search || "Reminder"}`;
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setLeadData(result?.data);
      setTotalPage(result.totalPages);
      setTodayCount(result?.totalTodayRemindersCount);
      setLeadCount({
        newLead: result.newCount,
        inProgress: result.withoutNewCount,
      });
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactLead = async (leadId: string, userId?: string) => {
    setLoading(true);
    try {
      let connectedId = userId;
      let currentUserName = "";
      let assignedUserName = "";
      
      // Get current user from token
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("User not authenticated");
        return;
      }
      const decodedToken: any = jwtDecode(token);
      connectedId = connectedId || decodedToken.userId;
      
      // Find current user name from user list
      if (user && Array.isArray(user)) {
        const currentUser = user.find((u) => u.id === decodedToken.userId);
        if (currentUser) currentUserName = currentUser.name;
        const assignedUser = user.find((u) => u.id === connectedId);
        if (assignedUser) assignedUserName = assignedUser.name;
      }
      
      // Fallback to token name if not found in user list
      if (!currentUserName && decodedToken.name)
        currentUserName = decodedToken.name;
      if (
        !assignedUserName &&
        connectedId === decodedToken.userId &&
        decodedToken.name
      )
        assignedUserName = decodedToken.name;
      
      const updateData = {
        leadStatus: "Contacted",
        connectedId,
      };

      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateLead}${leadId}`,
        data: updateData,
      });

      // Send message
      if (currentUserName && assignedUserName) {
        await ApiService({
          method: "POST",
          endpoint: apiPaths.messages.replace(":id", leadId),
          data: {
            message: `${currentUserName} to ${assignedUserName}`,
          },
        });
      }

      // Update the lead data directly in state
      setLeadData((prevLeads) =>
        prevLeads.map((lead) => {
          if (lead._id === leadId) {
            return {
              ...lead,
              connectedId: response.connectedId,
            };
          }
          return lead;
        })
      );

      setLeadName(response.connectedId.name);
      toast.success("Lead contacted successfully");
    } catch (error: any) {
      console.error("Error contacting lead:", error);
      toast.error(error?.response?.data?.message || "Error contacting lead");
    } finally {
      setLoading(false);
    }
  };

  const handleReminderToggle = async (row: any) => {
    try {
      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateLead}${row._id}`,
        data: { isReminder: !row.isReminder },
      });

      setLeadData((prevLeads) =>
        prevLeads.map((lead) =>
          lead._id === row._id
            ? { ...lead, isReminder: !lead.isReminder }
            : lead
        )
      );
      toast.success("Reminder status updated!");
    } catch (error) {
      toast.error("Failed to update reminder status");
    }
  };

  const handleUpdateContact = async (id: string, data: any) => {
    setLoading(true);
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateClient}${id}`,
        data,
      });
      toast.success("Contact updated successfully");
      handleDateFilter(null, null);
    } catch (error) {
      toast.error("Failed to update contact");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setPage(1);
    setSortValue("");
    setDateFilter("today");
    setCsvStartDate(new Date());
    setCsvEndDate(new Date());
    handleDateFilter(today, today);
  };

  // CSV Import functionality
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);

  const handleImportCsv = async (file: File) => {
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('csvFile', file);
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.importCsv,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      });

      setImportResults(response);
      
      if (response.summary.successCount > 0) {
        toast.success(`CSV import completed! ${response.summary.successCount} leads imported successfully.`);
      } else if (response.summary.errorCount > 0) {
        toast.error(`CSV import completed with ${response.errors[0].error}. Check the import results for details.`);
      } else {
        toast('CSV import completed but no leads were imported. Check the import results for details.', { icon: '⚠️' });
      }
      
      handleDateFilter(null, null);
      
      if (response.summary.successCount > 0) {
        setTimeout(() => {
          setIsImportModalOpen(false);
          setImportResults(null);
        }, 3000);
      }

    } catch (error: any) {
      console.error("CSV import error:", error);
      console.error("Error response:", error.response);
      console.error("Error data:", error.response?.data);
      toast.error(error.response?.data?.error || "Failed to import CSV file");
    } finally {
      setImportLoading(false);
    }
  };

  const openImportModal = () => {
    setIsImportModalOpen(true);
    setImportResults(null);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setImportResults(null);
  };

  return {
    user,
    userId,
    leadCount,
    selectedLeadId,
    leadData,
    showConfirmation,
    showDeleteConfirmation,
    leadName,
    isEmail,
    isEmailSent,
    setSelectedLeadId,
    setUserId,
    setLeadData,
    handleAdd,
    handleEdit,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    updateScreen,
    loading,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
    openEmailModal,
    handleSendEmail,
    handleSendComment,
    selectedRows,
    handleRowSelection,
    handleSelectStatus,
    isEmailModalOpen,
    setIsEmailModalOpen,
    setSelectedLeadForEmail,
    selectedLeadForEmail,
    handleSelectAll,
    sortValue,
    handleDeleteLead,
    handleConfirmDelete,
    handleCancelDelete,
    setSortValue,
    handleDownloadCSV,
    handleConfirmDownload,
    handleCancelDownload,
    csvStartDate,
    setCsvStartDate,
    csvEndDate,
    setCsvEndDate,
    handleRemainder,
    isDownloadModalOpen,
    todayCount,
    handleGraph,
    graphData,
    handleContactLead,
    handleReminderToggle,
    fetchServicesData,
    services,
    platforms,
    setPlatforms,
    fetchPlatformsData,
    clearFilters,
    handleUpdateContact,
    dateFilter,
    setDateFilter,
    isImportModalOpen,
    openImportModal,
    closeImportModal,
    importLoading,
    importResults,
    handleImportCsv,
  };
};

export default useLead;